"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getEndOfMonth = exports.getRanges = exports.convertStringToDate = exports.createDate = exports.getTimeRange = exports.getTime = exports.getStartOfDay = exports.getMonthDayRange = exports.getMonth = exports.getEarlierDate = exports.getLaterDate = exports.getEndOfDay = exports.getDayOfWeek = exports.getDateTimeString = exports.getDateString = exports.formatDate = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
const types_1 = require("../types");
const types_2 = require("../types");
/**
 * Get the later date between two dates.
 * @param pre The previous date.
 * @param cur The current date.
 * @param unit The unit of time to compare (default: 'milliseconds').
 * @returns The later date.
 */
function getLaterDate(pre, cur, unit = 'milliseconds') {
    return (0, dayjs_1.default)(pre).isAfter(cur, unit) ? pre : cur;
}
exports.getLaterDate = getLaterDate;
/**
 * Get the earlier date between two dates.
 * @param pre The previous date.
 * @param cur The current date.
 * @param unit The unit of time to compare (default: 'milliseconds').
 * @returns The earlier date.
 */
function getEarlierDate(pre, cur, unit = 'milliseconds') {
    return (0, dayjs_1.default)(pre).isBefore(cur, unit) ? pre : cur;
}
exports.getEarlierDate = getEarlierDate;
/**
 * Get the end of the day.
 * @param date The date to get the end of the day.
 * @param asDate A boolean indicating whether to return the date (default: false).
 * @param timezone The timezone to use (default: 'UTC').
 * @returns The end of the day.
 */
function getEndOfDay(date, asDate = false, timezone = types_1.DEFAULT_TIMEZONE) {
    const endOfDay = (0, dayjs_1.default)(date).tz(timezone).endOf(types_1.TimeUnit.DAY);
    return asDate ? endOfDay.toDate() : endOfDay.toISOString();
}
exports.getEndOfDay = getEndOfDay;
/**
 * Get the start of the day.
 * @param date The date to get the start of the day.
 * @param asDate A boolean indicating whether to return the date (default: false).
 * @param timezone The timezone to use (default: 'UTC').
 * @returns The start of the day.
 */
function getStartOfDay(date, asDate = false, timezone = types_1.DEFAULT_TIMEZONE) {
    const startOfDay = (0, dayjs_1.default)(date).tz(timezone).startOf(types_1.TimeUnit.DAY);
    return asDate ? startOfDay.toDate() : startOfDay.toISOString();
}
exports.getStartOfDay = getStartOfDay;
/**
 * Get the date string.
 * @param date The date to get the string.
 * @returns The date string.
 */
function getDateString(date) {
    if (typeof date === 'string') {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toISOString().split('T')[0];
        }
    }
    if (date instanceof Date) {
        if (!isNaN(date.getTime())) {
            return date.toISOString().split('T')[0];
        }
    }
    throw new types_2.CustomError(types_2.errorCode.invalidDate, 'Invalid Date');
}
exports.getDateString = getDateString;
/**
 * Get the date-time string.
 * @param date The date to get the string.
 * @returns The date-time string.
 */
function getDateTimeString(date) {
    if (typeof date === 'string') {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toISOString();
        }
    }
    if (date instanceof Date) {
        if (!isNaN(date.getTime())) {
            return date.toISOString();
        }
    }
    throw new types_2.CustomError(types_2.errorCode.invalidDate, 'Invalid Date');
}
exports.getDateTimeString = getDateTimeString;
/**
 * Format the date.
 * @param date The date to format.
 * @param format The format to apply.
 * @returns The formatted date.
 */
function formatDate(date, format) {
    let parsedDate;
    if (typeof date === 'string') {
        parsedDate = (0, dayjs_1.default)(date); // Convert string to Dayjs object
    }
    else if (date instanceof Date && !isNaN(date.getTime())) {
        parsedDate = (0, dayjs_1.default)(date); // Convert valid Date object to Dayjs object
    }
    else if (typeof date === 'number' || dayjs_1.default.isDayjs(date)) {
        parsedDate = (0, dayjs_1.default)(date); // Handle timestamp and Dayjs instances
    }
    else {
        throw new types_2.CustomError(types_2.errorCode.invalidDate, 'The provided value is not a valid Date object');
    }
    // Check if the parsed date is valid
    if (!parsedDate.isValid()) {
        throw new types_2.CustomError(types_2.errorCode.invalidDate, 'The provided value is not a valid Date object');
    }
    return parsedDate.utc().format(format).toLowerCase(); // Format the date
}
exports.formatDate = formatDate;
/**
 * Get the day of the week.
 * @param date The date to get the day of the week.
 * @param format The format to apply (default: 'ddd').
 * @returns The day of the week.
 */
function getDayOfWeek(date, format = types_1.DateTimeFormats.DAY) {
    return formatDate(date, format);
}
exports.getDayOfWeek = getDayOfWeek;
/**
 * Get the month.
 * @param date The date to get the month.
 * @param format The format to apply (default: 'MMM').
 * @returns The month.
 */
function getMonth(date, format = types_1.DateTimeFormats.MONTH) {
    return formatDate(date, format);
}
exports.getMonth = getMonth;
/**
 * Get the month day range.
 * @param startDate The start date.
 * @param formatStartDate The format for the start date (default: 'MMM D').
 * @param endDate The end date (default: startDate).
 * @param template The template to apply (default: DEFAULT_DATE_RANGE_TEMPLATE).
 * @returns The month day range.
 */
function getMonthDayRange(startDate, formatStartDate = types_1.DateTimeFormats.MMM_D, endDate, template = types_1.DEFAULT_DATE_RANGE_TEMPLATE) {
    const start = (0, dayjs_1.default)(startDate);
    if (!start.isValid()) {
        throw new types_2.CustomError(types_2.errorCode.invalidDate, 'Invalid start date');
    }
    const end = endDate ? (0, dayjs_1.default)(endDate) : start;
    if (endDate && !end.isValid()) {
        throw new types_2.CustomError(types_2.errorCode.invalidDate, 'Invalid end date');
    }
    const isSameMonth = start.month() === end.month();
    const startString = start.format(formatStartDate);
    const endString = isSameMonth ? end.format('D') : end.format(formatStartDate);
    const yearString = end.format('YYYY');
    // Conditionally replace placeholders based on their presence
    let result = template;
    if (template.includes('{{start}}')) {
        result = result.replace('{{start}}', startString);
    }
    if (template.includes('{{end}}')) {
        result = result.replace('{{end}}', endString);
    }
    if (template.includes('{{year}}')) {
        result = result.replace('{{year}}', yearString);
    }
    return result;
}
exports.getMonthDayRange = getMonthDayRange;
/**
 * Get the time.
 * @param date The date to get the time.
 * @param format The format to apply (default: 'h:mm A').
 * @param useUtc A boolean indicating whether to use UTC (default: false).
 * @returns The time.
 */
function getTime(date, format = types_1.DateTimeFormats.H12_AMPM, useUtc = false) {
    const d = useUtc ? dayjs_1.default.utc(date) : (0, dayjs_1.default)(date);
    if (!d.isValid()) {
        throw new types_2.CustomError(types_2.errorCode.invalidDate, 'Invalid date');
    }
    return d.format(format);
}
exports.getTime = getTime;
/**
 * Get the time range.
 * @param startDate The start date.
 * @param endDate The end date.
 * @param format The format to apply (default: 'h:mm A').
 * @param template The template to apply (default: DEFAULT_DATE_RANGE_TEMPLATE_START_END).
 * @param useUtc A boolean indicating whether to use UTC (default: false).
 * @returns The time range.
 */
function getTimeRange(startDate, endDate, format = types_1.DateTimeFormats.H12_AMPM, template = types_1.DEFAULT_DATE_RANGE_TEMPLATE_START_END, useUtc = false) {
    let result = template;
    try {
        if (template.includes('{{start}}')) {
            result = result.replace('{{start}}', getTime(startDate, format, useUtc));
        }
        if (template.includes('{{end}}')) {
            result = result.replace('{{end}}', getTime(endDate, format, useUtc));
        }
    }
    catch (error) {
        // Optionally handle the error more gracefully here
        throw new types_2.CustomError(types_2.errorCode.invalidDateRange, 'Invalid date range provided');
    }
    return result;
}
exports.getTimeRange = getTimeRange;
/**
 * Get the day ranges.
 * @param array The array of numbers.
 * @returns The day ranges.
 */
function getRanges(array) {
    if (!array.every(Number.isInteger)) {
        throw new types_2.CustomError(types_2.errorCode.invalidInteger, 'arrays must be an array of isIntegers');
    }
    if (array.length === 0) {
        return [];
    }
    // Deduplicate and sort the array
    const sortedArray = [...new Set(array)].sort((a, b) => a - b);
    return sortedArray.reduce((ranges, current, index, sortedArray) => {
        if (index === 0 || current !== sortedArray[index - 1] + 1) {
            ranges.push([current]);
        }
        else {
            ranges[ranges.length - 1][1] = current;
        }
        return ranges;
    }, []);
}
exports.getRanges = getRanges;
function createDate(year, month, day) {
    return new Date(year, month - 1, day);
}
exports.createDate = createDate;
function convertStringToDate(dateStr) {
    const [year, month, day] = dateStr.replace(/\//g, '-').split('-');
    return new Date(Number(year), Number(month) - 1, Number(day));
}
exports.convertStringToDate = convertStringToDate;
/**
 * Gets the end of the month for a given date.
 *
 * @param date - The input date.
 * @param asDate - If true, returns the result as a Date object. Otherwise, returns an ISO string. Default is false.
 * @param timezone - The timezone to use for the calculation. Default is DEFAULT_TIMEZONE.
 * @returns The end of the month as a Date object or an ISO string, depending on the value of `asDate`.
 */
function getEndOfMonth(date, asDate = false, timezone = types_1.DEFAULT_TIMEZONE) {
    const endOfMonth = (0, dayjs_1.default)(date).tz(timezone).endOf(types_1.TimeUnit.MONTH);
    return asDate ? endOfMonth.toDate() : endOfMonth.toISOString();
}
exports.getEndOfMonth = getEndOfMonth;
//# sourceMappingURL=dateUtil.js.map